import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import { uploadCurrentTenantDgiCredentials } from "@/services/authService";
import { formatDisplayDate } from "@/lib/dateFormat";
import {
  DGI_CREDENTIALS_ERROR_CODES,
  type DgiCredentialsErrorCode,
} from "@/lib/dgiCredentialErrorCodes";

export interface UseCertificateUploadOptions {
  tenantRut: string | null;
  onSuccess?: () => void | Promise<void>;
}

export function useCertificateUpload({
  tenantRut,
  onSuccess,
}: UseCertificateUploadOptions) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [passphrase, setPassphrase] = useState("");
  const [uploading, setUploading] = useState(false);
  /**
   * Set when the backend rejects the upload as expired, cleared on every new
   * attempt. Callers render the "request a new certificate" path off this —
   * without it an expired upload is a dead end with a red toast.
   */
  const [expiredOn, setExpiredOn] = useState<string | null>(null);

  const canSubmit = Boolean(
    file && tenantRut && passphrase.trim()
  );

  const reset = useCallback(() => {
    setFile(null);
    setPassphrase("");
    setExpiredOn(null);
  }, []);

  const submit = useCallback(async (): Promise<void> => {
    if (!file || !tenantRut) return;
    const trimmedPassphrase = passphrase.trim();
    if (!trimmedPassphrase) {
      toast.error(t("tenants.edit.dgi.passphraseRequired"));
      return;
    }

    setUploading(true);
    // Clear any previous expiry verdict: this is a new file, and leaving the
    // old one up would offer "request a new certificate" beside a cert we have
    // not judged yet.
    setExpiredOn(null);
    try {
      // Start the DGI envío sequence at 1. Tenants migrating from another provider
      // need to override this (their previous provider already burned 1..N); we'll
      // expose that override in the UI once the DGI-portal IdEmisor lookup is wired.
      await uploadCurrentTenantDgiCredentials(file, 1, trimmedPassphrase);
      await onSuccess?.();
      reset();
    } catch (err) {
      const data =
        err &&
        typeof err === "object" &&
        "response" in err
          ? (err as { response?: { data?: { errorCode?: string; message?: string; validTo?: string } } })
              .response?.data
          : undefined;
      const errorCode = data?.errorCode as DgiCredentialsErrorCode | undefined;
      const messageByCode: Partial<Record<DgiCredentialsErrorCode, string>> = {
        [DGI_CREDENTIALS_ERROR_CODES.CERTIFICATE_RUC_MISMATCH]: t(
          "tenants.edit.dgi.certificateRucMismatch",
          { lng: "es" }
        ),
        [DGI_CREDENTIALS_ERROR_CODES.CERTIFICATE_NO_RUC]: t(
          "tenants.edit.dgi.certificateNoRuc",
          { lng: "es" }
        ),
      };
      // Expiry gets its own branch: the date is the actionable part, and the
      // caller needs the code so it can offer "request a new one from Abitab"
      // rather than leaving a dead end.
      if (errorCode === DGI_CREDENTIALS_ERROR_CODES.CERTIFICATE_EXPIRED) {
        setExpiredOn(data?.validTo ?? null);
        toast.error(
          data?.validTo
            ? t("tenants.edit.dgi.certificateExpiredOn", {
                date: formatDisplayDate(data.validTo),
                lng: "es",
              })
            : t("tenants.edit.dgi.certificateExpired", { lng: "es" }),
        );
        return;
      }
      const translated = errorCode ? messageByCode[errorCode] : null;
      toast.error(translated ?? t("tenants.edit.dgi.errorUpload", { lng: "es" }));
    } finally {
      setUploading(false);
    }
  }, [file, tenantRut, passphrase, onSuccess, reset, t]);

  return {
    file,
    setFile,
    expiredOn,
    passphrase,
    setPassphrase,
    uploading,
    submit,
    reset,
    canSubmit,
  };
}
